// JobCardEditorPopover - Combined editor for job card content (title + summary)
// Single popover with tabs to edit both fields

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useContentVersions } from '../../../hooks/unified/useContentVersionsQueries';
import { GenerateForm } from './GenerateForm';
import { VersionHistoryList } from './VersionHistoryList';
import { ContentPreview } from './ContentPreview';
import type { ContentComponentType } from '../../../hooks/unified/useContentVersionsQueries';

interface JobCardEditorPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  onContentUpdated?: () => void;
}

type TabType = 'title' | 'summary';

export function JobCardEditorPopover({
  isOpen,
  onClose,
  jobId,
  onContentUpdated,
}: JobCardEditorPopoverProps) {
  const [activeTab, setActiveTab] = useState<TabType>('title');
  const modalRef = useRef<HTMLDivElement>(null);

  // Fetch versions for both title and summary
  const titleVersions = useContentVersions(jobId, 'title', { enabled: isOpen });
  const summaryVersions = useContentVersions(jobId, 'summary', { enabled: isOpen });

  const currentData = activeTab === 'title' ? titleVersions.data : summaryVersions.data;
  const isLoading = activeTab === 'title' ? titleVersions.isLoading : summaryVersions.isLoading;
  const error = activeTab === 'title' ? titleVersions.error : summaryVersions.error;

  // Handle escape key and click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    {
      id: 'title',
      label: 'Job Title',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
      ),
    },
    {
      id: 'summary',
      label: 'Summary',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
      ),
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 transition-opacity" />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full sm:max-w-2xl sm:mx-4 bg-white rounded-t-2xl sm:rounded-2xl shadow-xl 
                   transform transition-all animate-in fade-in slide-in-from-bottom sm:zoom-in-95 duration-200
                   max-h-[90vh] sm:max-h-none flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg hidden sm:block">
              <svg
                className="w-5 h-5 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                Edit Job Card Content
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">
                AI-powered editing for title and summary
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors min-h-[48px] ${
                activeTab === tab.id
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.icon}
              {tab.label}
              {/* Version count badge */}
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === tab.id
                  ? 'bg-purple-100 text-purple-600'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {tab.id === 'title' 
                  ? titleVersions.data?.total || 0 
                  : summaryVersions.data?.total || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-[60vh] sm:max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">Loading versions...</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="text-red-500 mb-2">Failed to load versions</div>
              <p className="text-sm text-gray-500">{error.message}</p>
            </div>
          ) : (
            <>
              {/* Current Active Content */}
              {currentData?.active && (
                <div className="px-6 py-4 bg-green-50 border-b border-green-100">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                      Active
                    </span>
                    <span className="text-sm text-gray-500">
                      Version {currentData.active.version_number}
                    </span>
                  </div>
                  <ContentPreview
                    content={currentData.active.content}
                    componentType={activeTab}
                    maxLines={activeTab === 'title' ? 1 : 3}
                  />
                </div>
              )}

              {/* Generate New Content */}
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Generate New {activeTab === 'title' ? 'Title' : 'Summary'}
                </h3>
                <GenerateForm
                  jobId={jobId}
                  componentType={activeTab as ContentComponentType}
                  onSuccess={() => {
                    onContentUpdated?.();
                  }}
                />
              </div>

              {/* Version History */}
              <div className="px-6 py-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Version History
                </h3>
                <VersionHistoryList
                  jobId={jobId}
                  componentType={activeTab as ContentComponentType}
                  versions={currentData?.history || []}
                  activeVersionId={currentData?.active?.id}
                  onActivated={() => {
                    onContentUpdated?.();
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
